<template>
	<section class="institution-demo">
		<div class="pc-center-1200">
			<div class="demo-grid" :class="{ 'demo-grid--with-detail': loading || isDetailVisible }">
				<div class="demo-left">
					<InstitutionSearchPanel
						:categories="categories"
						:active-category-id="activeCategoryId"
						:selected-region-code="selectedRegionCode"
						:regions="regions"
						:regions-loading="regionsLoading"
						:institutions="institutions"
						:selected-merchant-id="selectedMerchantId"
						:loading="loading"
						:placeholder="t(`${I18N_NS}.search.placeholder`)"
						:search-label="t(`${I18N_NS}.search.searchBtn`)"
						:result-count-text="resultCountText"
						:list-title="listTitle"
						@category-change="handleCategoryChange"
						@region-select="handleRegionSelect"
						@select-institution="handleSelectInstitution"
						@search="handleSearch"
					/>
				</div>
				<div v-if="loading || (isDetailVisible && detailLoading)" class="demo-detail-loading">
					<div class="detail-skeleton">
						<div class="sk sk-photo"></div>
						<div class="detail-skeleton-body">
							<div class="sk sk-title"></div>
							<div class="sk sk-subtitle"></div>
							<div class="sk sk-card"></div>
							<div class="sk sk-section-title"></div>
							<div class="sk sk-line"></div>
							<div class="sk sk-line"></div>
							<div class="sk sk-section-title"></div>
							<div class="sk sk-line"></div>
							<div class="sk sk-line sk-line-short"></div>
						</div>
					</div>
				</div>
				<InstitutionDetailPanel v-else-if="isDetailVisible && detail" v-bind="detail" />
			</div>
		</div>
	</section>
</template>

<script setup>
import InstitutionSearchPanel from "./search-panel.vue";
import { computed, defineAsyncComponent, onMounted, ref, watch } from "vue";
import { useI18n } from "~/composables/useI18n";
import { localeToApiLang } from "~/utils/request-lang";
import { getMerchantTypes } from "~/api/api";
import {
	useServiceInstitutionCache,
	useServiceInstitutionData,
} from "../../composables/useServiceInstitutionData";
import {
	getInstitutionParams,
	useServiceInstitutionTracking,
} from "../../composables/useServiceInstitutionTracking";
import catAll from "../../image/category/all.png";
import catNearby from "../../image/category/nearby.png";

const I18N_NS = "serviceInstitution.pc.demo";
const InstitutionDetailPanel = defineAsyncComponent(() => import("./detail-panel.vue"));

const { t, locale } = useI18n();
const { track } = useServiceInstitutionTracking("pc");
const detailSourceAction = ref("default");
const pageCache = useServiceInstitutionCache();

const getLangRequestConfig = lang => ({
	headers: {
		Lang: localeToApiLang(lang || locale.value),
	},
});

const fetchMerchantTypes = async lang => {
	const response = await getMerchantTypes(getLangRequestConfig(lang));
	if (response?.code !== 200 || !Array.isArray(response?.data)) {
		return [];
	}
	return response.data;
};

const { data: merchantTypes } = useAsyncData(
	() => `service-institution-merchant-types-${locale.value}`,
	async () => {
		const cacheKey = String(locale.value);
		if (Object.prototype.hasOwnProperty.call(pageCache.value.merchantTypes, cacheKey)) {
			return pageCache.value.merchantTypes[cacheKey];
		}

		const data = await fetchMerchantTypes(locale.value);
		pageCache.value.merchantTypes[cacheKey] = data;
		return data;
	},
	{
		default: () => pageCache.value.merchantTypes[String(locale.value)] || [],
		lazy: true,
		server: false,
		watch: [locale],
	}
);

const fixedCategories = computed(() => [
	{ id: "all", label: t(`${I18N_NS}.categories.all`), icon: catAll },
	{ id: "nearby", label: t(`${I18N_NS}.categories.nearby`), icon: catNearby },
]);

const categories = computed(() => {
	const apiCategories = (merchantTypes.value || [])
		.map(item => ({
			id: item.id,
			label: item.name,
			icon: item.small_icon_url || item.icon_url,
			color: item.color,
		}))
		.filter(item => item.id && item.label && item.icon);

	return [...fixedCategories.value, ...apiCategories];
});

const {
	activeCategoryId,
	selectedMerchantId,
	selectedRegionCode,
	selectedRegion,
	regions,
	regionsLoading,
	loading,
	detailLoading,
	institutions,
	detail,
	resultCountText,
	searchKeyword,
	setActiveCategory,
	selectRegion,
	selectMerchant,
	searchMerchants,
} = useServiceInstitutionData({ t, merchantTypes, locale });

const isDetailVisible = computed(() => institutions.value.length > 0);

const listTitle = computed(() => {
	if (searchKeyword.value.trim()) {
		return t(`${I18N_NS}.search.resultTitle`);
	}
	if (activeCategoryId.value === "nearby" && selectedRegion.value?.name) {
		return selectedRegion.value.name;
	}
	const category = getCategory(activeCategoryId.value);
	return category?.label || t(`${I18N_NS}.categories.all`);
});

function getCategory(categoryId) {
	return categories.value.find(item => String(item.id) === String(categoryId));
}

function getInstitution(merchantId) {
	return institutions.value.find(item => String(item.id) === String(merchantId));
}

function selectFirstInstitution() {
	const first = institutions.value[0];
	if (first) {
		detailSourceAction.value = "default";
		selectMerchant(first.id);
	}
}

async function handleCategoryChange(categoryId) {
	const previousCategory = getCategory(activeCategoryId.value);
	await setActiveCategory(categoryId);
	if (String(categoryId) !== "nearby") {
		selectFirstInstitution();
	}
	const category = getCategory(categoryId);

	track("service_category_filter_click", {
		category_id: categoryId,
		category_name: category?.label,
		previous_category: previousCategory?.label,
		result_count: institutions.value.length,
	});
}

async function handleRegionSelect(region) {
	await selectRegion(region);
	selectFirstInstitution();
	track("service_category_filter_click", {
		category_id: "nearby",
		category_name: region?.name,
		previous_category: t(`${I18N_NS}.categories.nearby`),
		result_count: institutions.value.length,
	});
}

async function handleSearch(keyword) {
	await searchMerchants(keyword);
	selectFirstInstitution();
	track("service_search_submit", {
		keyword,
		keyword_length: keyword.length,
		result_count: institutions.value.length,
		has_result: institutions.value.length > 0,
	});
}

function handleSelectInstitution(merchantId, sourceAction = "result_card") {
	const institution = getInstitution(merchantId);
	if (sourceAction === "result_card") {
		track(
			"institution_result_click",
			getInstitutionParams(institution, {
				position: institutions.value.findIndex(item => String(item.id) === String(merchantId)) + 1,
			})
		);
	}
	detailSourceAction.value = sourceAction;
	selectMerchant(merchantId);
}

onMounted(() => {
	track("service_org_page_view");
});

watch(
	() => detail.value?.id,
	id => {
		if (!id || !detail.value) return;
		track(
			"institution_detail_view",
			getInstitutionParams(detail.value, {
				source_action: detailSourceAction.value,
			})
		);
	},
	{ flush: "post" }
);
</script>

<style lang="scss" scoped>
.institution-demo {
	padding: 40px 0 56px;
	background: #f5f5f5;
}

.demo-grid {
	display: grid;
	grid-template-columns: minmax(0, 1fr);
	gap: 24px;
	align-items: stretch;
	width: 100%;
	max-width: 1200px;
	margin: 0 auto;
}

.demo-grid--with-detail {
	grid-template-columns: minmax(0, 752fr) minmax(0, 584fr);
	min-height: 1268px;
}

.demo-left,
:deep(.detail-panel) {
	min-width: 0;
	background: #fff;
	border-radius: 20px;
	box-shadow: 0 4px 24.6px rgba(0, 0, 0, 0.1);
}

.demo-left {
	display: flex;
	flex-direction: column;
	height: 1268px;
	overflow: hidden;
}

.demo-detail-loading {
	min-height: 1268px;
	background: #fff;
	border-radius: 20px;
	box-shadow: 0 4px 24.6px rgba(0, 0, 0, 0.1);
	padding: 24px;
}

.detail-skeleton {
	display: flex;
	flex-direction: column;
	gap: 20px;
	width: 100%;
}

.detail-skeleton-body {
	display: flex;
	flex-direction: column;
	gap: 14px;
}

.sk {
	position: relative;
	overflow: hidden;
	background: #f1f2f4;
	border-radius: 10px;
}

.sk::after {
	content: "";
	position: absolute;
	inset: 0;
	transform: translateX(-100%);
	background: linear-gradient(
		90deg,
		rgba(255, 255, 255, 0),
		rgba(255, 255, 255, 0.6),
		rgba(255, 255, 255, 0)
	);
	animation: sk-shimmer 1.4s infinite;
}

.sk-photo {
	height: 340px;
	border-radius: 16px;
}

.sk-title {
	width: 58%;
	height: 32px;
}

.sk-subtitle {
	width: 32%;
	height: 22px;
}

.sk-card {
	height: 120px;
	margin-top: 4px;
}

.sk-section-title {
	width: 28%;
	height: 24px;
	margin-top: 8px;
}

.sk-line {
	height: 16px;
}

.sk-line-short {
	width: 72%;
}

@keyframes sk-shimmer {
	100% {
		transform: translateX(100%);
	}
}
</style>
